import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { LogoComponent } from '@shared/components/logo/logo.component';
import { AppInputMaskDirective } from '@shared/directives/input-mask.directive';
import { createTwoFaMask } from '@shared/utils/inputMasks';
import { AlertComponent, GoogleIdentityService } from '@stillmandigital/common';
import { LoginRequest } from 'app/api/client/model/loginRequest';
import { AuthStore } from 'app/auth/shared/store/auth.signal-store';
import { environment } from 'environments/environment';
import { RecaptchaEnterpriseApi } from './login-page.interfaces';

declare global {
  interface Window {
    grecaptcha?: RecaptchaEnterpriseApi;
  }
}

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LogoComponent,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    AlertComponent,
    TranslateModule,
    AppInputMaskDirective,
  ],
})
export class LoginPageComponent {
  private readonly _fb = inject(FormBuilder);
  private readonly _store = inject(AuthStore);
  private readonly _googleIdentity = inject(GoogleIdentityService);

  public readonly twoFaMask = createTwoFaMask();

  public readonly form = this._fb.nonNullable.group({
    login: ['', Validators.required],
    password: ['', Validators.required],
    twoFACode: [''],
  });

  public readonly twoFactorForm = this._fb.nonNullable.group({
    twoFACode: ['', Validators.required],
  });

  public readonly errorMessage = this._store.loginError;

  public readonly ssoStep = this._store.ssoStep;

  public readonly ssoEmail = this._store.ssoEmail;

  public readonly ssoPending = this._store.ssoPending;

  public readonly googleSsoEnabled =
    environment.googleSsoEnabled && !!environment.googleClientId;

  public readonly googleReady = signal(false);

  private readonly _googleButton = viewChild<ElementRef<HTMLDivElement>>('googleButton');

  constructor() {
    afterNextRender(() => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${environment.recaptchaSitekey}`;
      script.async = true;
      document.head.appendChild(script);

      if (this.googleSsoEnabled) {
        this._initGoogleIdentity();
      }
    });
  }

  // The host element is recreated when the form returns from the 2FA step, so the button must be redrawn.
  private readonly _renderGoogleButton = effect(() => {
    const host = this._googleButton()?.nativeElement;

    if (this.googleReady() && host && host.childElementCount === 0) {
      this._googleIdentity.renderButton(host, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        width: 400,
      });
    }
  });

  public submitTwoFactor() {
    const credential = this._store.ssoCredential();

    if (this.twoFactorForm.invalid || !credential) {
      return;
    }

    const twoFACode = this.twoFactorForm.getRawValue().twoFACode.replace(/\s/g, '');

    this._store.googleLogin({ credential, twoFACode });
  }

  public useAnotherAccount() {
    this.twoFactorForm.reset();
    this._store.cancelGoogleLogin();
  }

  public clearError() {
    this._store.clearLoginError();
  }

  public login() {
    this.clearError();

    window.grecaptcha?.enterprise.ready(() => {
      window
        .grecaptcha!.enterprise.execute(environment.recaptchaSitekey, { action: 'login' })
        .then(token => this._submitLogin(token))
        .catch(err => {
          Sentry.captureException(err);
        });
    });
  }

  private _initGoogleIdentity() {
    this._googleIdentity
      .load()
      .then(() => {
        this._googleIdentity.initialize(environment.googleClientId, credential =>
          this._store.googleLogin({ credential }),
        );
        this.googleReady.set(true);
      })
      .catch((error: Error) => Sentry.captureException(error));
  }

  private _submitLogin(captchaToken: string) {
    const { login, password, twoFACode: rawCode } = this.form.value;
    const twoFACode = rawCode?.replace(/\s/g, '');

    this._store.login({
      login,
      password,
      twoFACode,
      captchaToken,
      captchaType: LoginRequest.CaptchaTypeEnum.RecaptchaCloud,
    });
  }
}
